import { baseUri } from "./cfg";
import * as c from "../crypto";
import d from "../debug";
import asHTML from "../template";

import * as Spt from "@spotify/web-api-ts-sdk";
import type { AccessToken } from "@spotify/web-api-ts-sdk";
import type { Player, AppState, OnStateChange } from "./lib";

const getTime = () => new Date().getTime();
const redirectUri = `${baseUri}/`;
const domTrackInfo = document.getElementById("player-info");

declare global {
  interface Window {
    player: Spotify.Player;
  }
}

/////////////
/// AUTH ///
///////////

const CLIENT_ID = "b98634040da64acfa011e6a9a5e4cf6c";

export const authorizeApp = async function () {
  const codeVerifier = c.generateRandomString(64);
  window.localStorage.setItem("code_verifier", codeVerifier);
  d(1, "set code verifier to", codeVerifier);

  const hashed = await c.sha256(codeVerifier);
  const codeChallenge = c.base64encode(hashed);

  const scope = "streaming user-read-email user-read-private";
  const authUrl = new URL("https://accounts.spotify.com/authorize");

  const params = {
    response_type: "code",
    client_id: CLIENT_ID,
    scope,
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
    redirect_uri: redirectUri,
  };

  authUrl.search = new URLSearchParams(params).toString();
  window.location.href = authUrl.toString();
};

const makeToken = async (code: string, codeVerifier: string) => {
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      client_id: CLIENT_ID,
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    }),
  };
  const body = await fetch("https://accounts.spotify.com/api/token", payload);
  const result = await body.json();
  return result;
};

const refreshToken = async (token: AccessToken) => {
  const url = "https://accounts.spotify.com/api/token";

  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: token.refresh_token,
      client_id: CLIENT_ID,
    }),
  };

  const body = await fetch(url, payload);
  const response = await body.json();
  d(1, "refreshed token", { response });
  return response;
};

const storeToken = (token: AccessToken, time: number = getTime()) => {
  localStorage.setItem("token", JSON.stringify(token));
  localStorage.setItem("tokenTime", String(time));
};

const _getToken = async (): Promise<AccessToken | null> => {
  const urlParams = new URLSearchParams(window.location.search);
  history.replaceState(null, "", location.pathname);
  const code = urlParams.get("code");
  const codeVerifier = localStorage.getItem("code_verifier");
  let storageTokenStr = localStorage.getItem("token");

  if (!storageTokenStr) {
    if (code && codeVerifier) {
      const token = await makeToken(code, codeVerifier);
      storeToken(token);
      return token;
    }
    // we need to authorize. that is opt in through a button, not to be done
    // here.
    return null;
  } else {
    const token = JSON.parse(storageTokenStr);
    const tokenTime = Number(localStorage.getItem("tokenTime"));
    if (getTime() - tokenTime > 3540 * 1000) {
      alert("btw we need a new token lol");
      const newToken = await refreshToken(token);
      storeToken(newToken);
      return newToken;
    } else {
      return token;
    }
  }
};

let tokenPromise: Promise<AccessToken | null> | null = null;
const getToken = async () => {
  if (tokenPromise) {
    const result = await tokenPromise;
    return result;
  } else {
    tokenPromise = _getToken();
    const result = await tokenPromise;
    tokenPromise = null;
    return result;
  }
};

/// player

const _player: Promise<Spotify.Player> = new Promise((resolve, reject) => {
  window.onSpotifyWebPlaybackSDKReady = async () => {
    const token = await getToken();
    if (token) {
      const player = new Spotify.Player({
        name: "song-learner",
        volume: 0.3,
        getOAuthToken: (cb) => {
          cb(token.access_token);
        },
      });
      player.addListener("not_ready", ({ device_id }) => {
        d(1, "Device ID has gone offline", device_id);
      });

      player.addListener("initialization_error", ({ message }) => {
        console.error(message);
      });

      player.addListener("authentication_error", async ({ message }) => {
        console.error(message);
        // try getting the token again.
        const newToken = await getToken();
        if (newToken) {
          storeToken(newToken);
          player.connect(); //?
        }
      });

      player.addListener("account_error", ({ message }) => {
        console.error(message);
      });

      player.connect();
      window.player = player;
      resolve(player);
    } else {
      reject(new Error("no token"));
    }
  };
});

export async function initializePlayer(): Promise<Player | null> {
  const playerRep = asHTML<HTMLImageElement>`<img class="track-art" />`;
  document.getElementById("player-rep")?.appendChild(playerRep);

  const onStateChange: { fn: null | OnStateChange } = { fn: null };
  function registerStateChange(fn: OnStateChange) {
    onStateChange.fn = fn;
  }

  const token = await getToken();
  if (!token) {
    const newEl = document.createElement("button");
    newEl.innerHTML = "authorize app";
    newEl.addEventListener("click", () => {
      authorizeApp();
    });
    (document.querySelector("main") as HTMLDivElement).appendChild(newEl);
    return null;
  }
  const player = await _player;

  player.addListener("ready", ({ device_id }) => {
    const transferPlaybackToHere = async () => {
      const token = await getToken();
      if (!token) {
        console.error("not authenticated. cannot transfer playback.");
        return;
      }

      const sdk = Spt.SpotifyApi.withAccessToken(CLIENT_ID, token);
      sdk.player.transferPlayback([device_id], true);
    };

    transferPlaybackToHere();
  });

  var _playbackState: Spotify.PlaybackState | null = null;

  player.addListener("player_state_changed", (playbackState) => {
    onStateChange.fn?.(transformState(playbackState));

    if (
      _playbackState?.track_window.current_track.id !==
      playbackState?.track_window.current_track.id
    ) {
      domTrackInfo?.replaceChildren(renderPlayerInfo(playbackState));
    }

    _playbackState = playbackState;
  });

  updatePosition(player, onStateChange);

  return {
    onStateChange: registerStateChange,
    seek: player.seek.bind(player),
    next: player.nextTrack.bind(player),
    prev: player.previousTrack.bind(player),
    pause: player.pause.bind(player),
    togglePlay: player.togglePlay.bind(player),
  };
}

function renderPlayerInfo(
  playbackState: Spotify.PlaybackState | null,
): HTMLDivElement {
  if (!playbackState) {
    return asHTML`<div>no track...</div>`;
  }
  const track = playbackState.track_window.current_track;
  return asHTML`
<div id="spotify-info">
  <img id="track-art" src="${track.album.images[0].url}" />
  <h1 class="track-name">${track.name}</h1>
  <h2 class="album-name">${track.album.name}</h2>
</div>`;
}

function transformState(
  playbackState: Spotify.PlaybackState | null,
): Partial<AppState> {
  if (!playbackState) {
    return {}; // no updates
  }

  return {
    position: playbackState.position,
    duration: playbackState.duration,
    playing: !(
      playbackState.loading ||
      playbackState.playback_speed === 0 ||
      playbackState.paused
    ),
  };
}

async function updatePosition(
  player: Spotify.Player,
  onStateChange: { fn: OnStateChange | null },
) {
  onStateChange.fn?.({ position: (await player.getCurrentState())?.position });
  requestAnimationFrame(updatePosition.bind(null, player, onStateChange));
}
